<script>
  import QRCode from 'qrcode';
  import {
    accountTicketSection,
    computeTicketUsage,
    isOrderPaid,
    ticketTitleFromOrder,
    formatPrice,
    formatDate
  } from './account-ticket.js';

  export let orders = [];

  // Размеры QR.
  // Превью — единый источник, CSS масштабирует под брейкпоинты (200→180).
  // 512 — для скачиваемого PNG.
  const QR_PREVIEW_PX = 240;
  const QR_DOWNLOAD_PX = 512;

  // Кэш превью по redeem_token (а не по order.id), чтобы при ре-рендере не
  // дёргать QRCode.toDataURL заново и не плодить async-работу.
  let previewByToken = {};

  async function ensurePreview(token) {
    if (!token || previewByToken[token]) return;
    try {
      const dataUrl = await QRCode.toDataURL(token, {
        width: QR_PREVIEW_PX,
        margin: 2,
        errorCorrectionLevel: 'M'
      });
      previewByToken = { ...previewByToken, [token]: dataUrl };
    } catch (e) {
      console.warn('[QR] preview render failed:', e?.message || e);
    }
  }

  async function downloadQR(order) {
    const token = order?.redeem_token;
    if (!token) return;
    try {
      const dataUrl = await QRCode.toDataURL(token, {
        width: QR_DOWNLOAD_PX,
        margin: 2,
        errorCorrectionLevel: 'M'
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `forum-industry-ticket-${order.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.warn('[QR] download failed:', e?.message || e);
    }
  }

  function onQrKeydown(event, order) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      downloadQR(order);
    }
  }

  $: sorted = [...(orders ?? [])].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });

  $: sorted.forEach((o) => o?.redeem_token && ensurePreview(o.redeem_token));

  $: hasOrders = sorted.length > 0;
</script>

<section class="ticket-section" aria-labelledby="ticket-title">
  <header class="ticket-section-head">
    <div class="ticket-section-titles">
      <p class="kicker">{accountTicketSection.card.kicker}</p>
      <h2 id="ticket-title">
        {hasOrders ? accountTicketSection.card.sectionTitle : accountTicketSection.card.sectionTitleEmpty}
      </h2>
    </div>
    <a class="help-link" href={accountTicketSection.card.helpCta.href}>
      {accountTicketSection.card.helpCta.label}
    </a>
  </header>

  {#if !hasOrders}
    <div class="empty">
      <p>{accountTicketSection.empty.description}</p>
      <a class="empty-cta" href={accountTicketSection.empty.cta.href}>
        {accountTicketSection.empty.cta.label}
      </a>
    </div>
  {:else}
    {#each sorted as order (order.id)}
      {@const paid = isOrderPaid(order)}
      {@const usage = computeTicketUsage(order)}
      {@const previewSrc = order?.redeem_token ? previewByToken[order.redeem_token] : null}
      <article class="ticket-card">
        <span class="cut-notch cut-notch-left" aria-hidden="true"></span>
        <span class="cut-notch cut-notch-right" aria-hidden="true"></span>

        <div class="ticket-grid">
          <!-- Левая колонка: статус, тариф, DL, QR -->
          <div class="ticket-main">
            <div class="ticket-meta-row">
              <span class="status" class:status-ok={paid} class:status-warn={!paid}>
                <span class="status-dot" aria-hidden="true"></span>
                {order?.status?.name || (paid ? accountTicketSection.status.paid.label : accountTicketSection.status.pending.label)}
              </span>
              <span class="meta-dim">Заказ №{order.id}</span>
              <span class="meta-dim">Куплен {formatDate(order.created_at)}</span>
            </div>

            <div class="ticket-body">
              <div class="ticket-text">
                <p class="field-label">{accountTicketSection.card.tariffLabel}</p>
                <h3 class="ticket-name">{ticketTitleFromOrder(order)}</h3>

                <dl class="ticket-dl">
                  <div>
                    <dt>{accountTicketSection.card.passesLabel}</dt>
                    <dd>{usage?.passesLine ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>{accountTicketSection.card.priceLabel}</dt>
                    <dd>{formatPrice(order.price)}</dd>
                  </div>
                  <div>
                    <dt>{accountTicketSection.card.statusLabelLabel}</dt>
                    <dd>{usage?.statusLine ?? (paid ? 'Готов' : 'Ожидает оплаты')}</dd>
                  </div>
                </dl>

                {#if !paid && order.payment_link}
                  <a class="pay-cta" href={order.payment_link} target="_blank" rel="noreferrer">
                    {accountTicketSection.card.payCta}
                  </a>
                {/if}
              </div>

              {#if paid && usage}
                <div class="qr-block">
                  <div
                    class="qr-frame"
                    class:qr-grayed={usage.grayedOut}
                    class:qr-placeholder={!usage.showQr || !previewSrc}
                    aria-label={accountTicketSection.card.qrAriaLabel}
                  >
                    {#if usage.showQr && previewSrc}
                      <img
                        class="qr-img"
                        src={previewSrc}
                        alt={accountTicketSection.usage.qrAlt(order.id)}
                        role="button"
                        tabindex="0"
                        title={accountTicketSection.card.qrHint}
                        on:click={() => downloadQR(order)}
                        on:keydown={(e) => onQrKeydown(e, order)}
                      />
                    {:else if usage.showQr && !previewSrc}
                      <span class="qr-stub-spinner" aria-hidden="true"></span>
                      <span class="visually-hidden">Готовим QR-код…</span>
                    {:else}
                      <span class="qr-stub-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="2"/>
                          <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="2"/>
                          <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="2"/>
                          <path d="M14 14h3v3M21 14v3M14 18v3h3M21 21h-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                      </span>
                    {/if}
                  </div>
                  <button
                    type="button"
                    class="qr-hint"
                    disabled={!usage.showQr || !previewSrc}
                    on:click={() => downloadQR(order)}
                  >
                    {accountTicketSection.card.qrHint}
                  </button>
                </div>
              {/if}
            </div>
          </div>

          <!-- Правая колонка: памятка -->
          {#if paid}
            <aside class="ticket-memo">
              <span class="memo-cut-line" aria-hidden="true"></span>
              <p class="field-label">{accountTicketSection.card.memoLabel}</p>
              <ol class="memo-list">
                {#each accountTicketSection.card.memoSteps as step, i}
                  <li>
                    <span class="memo-num" class:memo-num-primary={i === 0} aria-hidden="true">{i + 1}</span>
                    <p>{step}</p>
                  </li>
                {/each}
              </ol>
            </aside>
          {/if}
        </div>
      </article>
    {/each}
  {/if}
</section>

<style>
  .ticket-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ticket-section-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
  }

  .ticket-section-titles {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .kicker {
    margin: 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  h2 {
    margin: 0;
    color: #ffffff;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }

  .help-link {
    color: #e7b962;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: color 160ms ease;
    white-space: nowrap;
  }

  .help-link:hover,
  .help-link:focus-visible {
    color: #ffa800;
  }

  /* ============== Empty ============== */
  .empty {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 28px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    background: transparent;
  }

  .empty p {
    margin: 0;
    color: rgba(255, 255, 255, 0.55);
    font-size: 14px;
    line-height: 22px;
  }

  .empty-cta {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    padding: 10px 18px;
    border: 0;
    border-radius: 8px;
    background: #e7b962;
    color: #000;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background-color 160ms ease;
  }

  .empty-cta:hover,
  .empty-cta:focus-visible {
    background: #ffa800;
  }

  /* ============== Ticket card ============== */
  .ticket-card {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.025);
  }

  /* «Выемки» по бокам как у бумажного билета */
  .cut-notch {
    position: absolute;
    top: 50%;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #000000;
    transform: translateY(-50%);
    z-index: 5;
  }

  .cut-notch-left {
    left: -14px;
  }

  .cut-notch-right {
    right: -14px;
  }

  .ticket-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 270px;
    align-items: stretch;
  }

  .ticket-main {
    padding: 40px;
    min-width: 0;
  }

  .ticket-meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .status {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .status-ok {
    color: #7ed321;
  }

  .status-warn {
    color: #e7b962;
  }

  .meta-dim {
    color: rgba(255, 255, 255, 0.35);
    font-size: 12px;
  }

  .ticket-body {
    margin-top: 32px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 200px;
    align-items: start;
    gap: 32px;
  }

  .ticket-text {
    min-width: 0;
  }

  .field-label {
    margin: 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  .ticket-name {
    margin: 8px 0 0;
    max-width: 520px;
    color: #ffffff;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.005em;
  }

  .ticket-dl {
    margin: 28px 0 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .ticket-dl > div {
    min-width: 0;
  }

  .ticket-dl dt {
    margin: 0;
    color: rgba(255, 255, 255, 0.35);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .ticket-dl dd {
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    line-height: 20px;
  }

  .pay-cta {
    margin-top: 28px;
    display: inline-flex;
    align-items: center;
    padding: 10px 18px;
    border-radius: 8px;
    background: #e7b962;
    color: #000;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: background-color 160ms ease;
  }

  .pay-cta:hover,
  .pay-cta:focus-visible {
    background: #ffa800;
  }

  /* ============== QR ============== */
  .qr-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
  }

  .qr-frame {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 200px;
    height: 200px;
    padding: 12px;
    border-radius: 8px;
    background: #ffffff;
    box-sizing: border-box;
  }

  .qr-frame.qr-placeholder {
    background: rgba(255, 255, 255, 0.04);
    border: 1px dashed rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.45);
  }

  .qr-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: pointer;
    border-radius: 4px;
    transition: opacity 160ms ease;
  }

  .qr-img:focus-visible {
    outline: 2px solid #e7b962;
    outline-offset: 4px;
  }

  .qr-frame.qr-grayed .qr-img {
    opacity: 0.4;
  }

  .qr-stub-icon svg {
    width: 64px;
    height: 64px;
  }

  .qr-stub-spinner {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: rgba(255, 255, 255, 0.6);
    animation: spin 0.9s linear infinite;
  }

  /* keyframes scoped per-component в Svelte 3 — нужен локальный. */
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .qr-hint {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(255, 255, 255, 0.45);
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    cursor: pointer;
    transition: color 160ms ease;
    font-family: inherit;
  }

  .qr-hint:hover:not([disabled]),
  .qr-hint:focus-visible:not([disabled]) {
    color: #ffffff;
  }

  .qr-hint[disabled] {
    cursor: default;
  }

  /* ============== Memo (right column) ============== */
  .ticket-memo {
    position: relative;
    padding: 32px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
  }

  /* Пунктирный «отрезной» вертикальный шов */
  .memo-cut-line {
    position: absolute;
    top: 32px;
    bottom: 32px;
    left: -1px;
    width: 1px;
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.16) 45%,
      transparent 0%
    );
    background-size: 1px 12px;
    background-repeat: repeat-y;
    background-position: center;
    pointer-events: none;
  }

  .memo-list {
    margin: 20px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .memo-list li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .memo-list li p {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    line-height: 20px;
  }

  .memo-num {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    font-weight: 700;
  }

  .memo-num-primary {
    background: #e7b962;
    color: #000000;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  /* ============== Tablet (≤1279px) ============== */
  @media (max-width: 1279px) {
    h2 {
      font-size: 22px;
    }

    .ticket-grid {
      grid-template-columns: 1fr;
    }

    .ticket-main {
      padding: 32px;
    }

    .ticket-body {
      grid-template-columns: 1fr 180px;
      gap: 24px;
    }

    .ticket-name {
      font-size: 18px;
    }

    .qr-frame {
      width: 180px;
      height: 180px;
    }

    .ticket-memo {
      padding: 32px;
      border-left: 0;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .memo-cut-line {
      display: none;
    }
  }

  /* ============== Mobile (≤767px) ============== */
  @media (max-width: 767px) {
    h2 {
      font-size: 20px;
    }

    .empty,
    .ticket-main,
    .ticket-memo {
      padding: 20px;
    }

    .ticket-body {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .qr-block {
      align-items: flex-start;
    }

    .qr-frame {
      width: 180px;
      height: 180px;
    }

    .qr-hint {
      text-align: left;
    }

    .ticket-dl {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .pay-cta {
      width: 100%;
      justify-content: center;
    }
  }
</style>
